import { Component, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { Subscription } from 'rxjs';
import {
  StateService,
  ToolService,
  RouterService,
  WorkDay,
  WorkingOverviewRequest,
  AbsenceEntry,
  ModalService,
} from 'projects/shared/src/public-api';
import { ContextMenuOption } from 'projects/shared/src/lib/models/context-menu-option';
import { ModalRole } from '../../models/modal-roles';
import { WorkListEntry } from '../../models/work-list-entry';

@Component({
  selector: 'lib-hours-overview',
  templateUrl: './hours-overview.component.html',
  styleUrls: ['./hours-overview.component.scss'],
  standalone: false,
})
export class HoursOverviewComponent implements OnInit {
  private translate = inject(TranslateService);

  constructor(
    public state: StateService,
    public toolService: ToolService,
    public routerService: RouterService,
    public modalService: ModalService
  ) {}
  private subs: Subscription[] = [];
  private allWorkDays: WorkDay[] = [];
  public filteredWorkListEntries: WorkListEntry[] = [];
  private absenceEntries: AbsenceEntry[] = [];
  private selectedConSite: string;
  private selectedWorker: string;
  private selectedMonth: number = new Date().getMonth();
  private selectedYear: number = new Date().getFullYear();
  public sumAll: string;
  public sumApproved: string;
  private sumAbsence: string;
  public showAbsenceEntries: boolean = false;
  private initialized: boolean = false;

  async ngOnInit() {
    this.showAbsenceEntries =
      (await this.state.readFromSecureStorage(
        this.state.SHOWABSENCEENTRIESPREFERENCE
      )) == '1';
    this.subs.push(
      this.state.workingDays$.subscribe((x) => {
        this.allWorkDays = x;
        if (!this.state.userHasMinType(1) && !this.initialized) {
          this.selectedWorker = this.state.userName;
        } else {
          this.selectedWorker = null;
        }
        this.filterWorkDays();
        this.initialized = true;
      })
    );
    this.subs.push(
      this.state.absenceEntries$.subscribe((x) => {
        this.absenceEntries = x;
        this.filterWorkDays();
      })
    );
  }

  ngOnDestroy() {
    this.subs?.forEach((x) => x.unsubscribe());
  }

  public userChange(e: any) {
    this.selectedWorker = e;
    this.filterWorkDays();
  }

  conSiteChange(conSiteId: string) {
    this.selectedConSite = conSiteId;
    this.filterWorkDays();
  }

  monthChange(month: number) {
    this.selectedMonth = month;
    this.filterWorkDays();
  }

  yearChange(year: number) {
    this.selectedYear = year;
    this.filterWorkDays();
  }

  getSum(wd: WorkDay): string {
    if (this.selectedConSite != null) {
      let entry = wd.workEntries.find(
        (x) => x.constructionSite === this.selectedConSite
      );
      return (
        this.toolService.addLeadingZero(entry.durationInHours) +
        ':' +
        this.toolService.addLeadingZero(entry.durationInMins) +
        'H'
      );
    } else {
      return (
        this.toolService.addLeadingZero(wd.durationInHours) +
        ':' +
        this.toolService.addLeadingZero(wd.durationInMins) +
        'H'
      );
    }
  }

  isNotApproved(wd: WorkDay): boolean {
    return wd.workEntries.find((x) => !x.approvedBy) != null;
  }

  async filterWorkDays() {
    await this.state.saveToSecureStorage(
      this.state.SHOWABSENCEENTRIESPREFERENCE,
      this.showAbsenceEntries ? '1' : '0'
    );
    this.filteredWorkListEntries = [];
    let filteredWorkDays = this.allWorkDays;
    if (this.selectedConSite != null) {
      filteredWorkDays = filteredWorkDays.filter(
        (x) =>
          x.workEntries.find(
            (y) => y.constructionSite == this.selectedConSite
          ) != null
      );
    }
    if (this.selectedWorker != null) {
      filteredWorkDays = filteredWorkDays.filter(
        (x) => x.user == this.selectedWorker
      );
    }
    if (this.selectedMonth != null) {
      filteredWorkDays = filteredWorkDays.filter(
        (x) => new Date(x.workingDate).getMonth() == this.selectedMonth
      );
    }
    if (this.selectedYear != null) {
      filteredWorkDays = filteredWorkDays.filter(
        (x) => new Date(x.workingDate).getFullYear() == this.selectedYear
      );
    }
    let sumHoursAll = 0;
    let sumMinutesAll = 0;
    let sumHoursApproved = 0;
    let sumMinutesApproved = 0;
    for (let item of filteredWorkDays) {
      if (this.selectedConSite != null) {
        const conSiteWorkEntry = item.workEntries.find(
          (x) => x.constructionSite == this.selectedConSite
        );
        sumHoursAll += conSiteWorkEntry.durationInHours;
        sumMinutesAll += conSiteWorkEntry.durationInMins;
        if (conSiteWorkEntry.approvedBy) {
          sumHoursApproved += conSiteWorkEntry.durationInHours;
          sumMinutesApproved += conSiteWorkEntry.durationInMins;
        }
      } else {
        item.workEntries.forEach((x) => {
          sumHoursAll += x.durationInHours;
          sumMinutesAll += x.durationInMins;
          if (x.approvedBy) {
            sumHoursApproved += x.durationInHours;
            sumMinutesApproved += x.durationInMins;
          }
        });
      }
      this.filteredWorkListEntries.push({
        id: item.id,
        date: item.workingDate,
        name: this.state.getUserDisplayName(item.user),
        hours: this.getSum(item),
        isAbsence: false,
        approved: !this.isNotApproved(item),
        workingHoursComplete: item.workingHoursComplete,
      });
    }
    sumHoursAll = sumHoursAll + Math.floor(sumMinutesAll / 60);
    sumMinutesAll = sumMinutesAll % 60;
    sumHoursApproved = sumHoursApproved + Math.floor(sumMinutesApproved / 60);
    sumMinutesApproved = sumMinutesApproved % 60;

    this.sumAll = this.translate.instant('hours.totalValue', {
      value: `${this.toolService.addLeadingZero(
        sumHoursAll
      )}:${this.toolService.addLeadingZero(sumMinutesAll)}H`,
    });
    this.sumApproved = this.translate.instant('hours.approvedValue', {
      value: `${this.toolService.addLeadingZero(
        sumHoursApproved
      )}:${this.toolService.addLeadingZero(sumMinutesApproved)}H`,
    });

    // Todo Abwesenheitszeiten Summe anzeigen
    let filteredAbsenceEntries = this.absenceEntries;

    if (this.selectedMonth != null) {
      filteredAbsenceEntries = filteredAbsenceEntries.filter(
        (x) => new Date(x.timeEntry.start).getMonth() == this.selectedMonth
      );
    }

    if (this.selectedYear != null) {
      filteredAbsenceEntries = filteredAbsenceEntries.filter(
        (x) => new Date(x.date).getFullYear() == this.selectedYear
      );
    }

    if (this.selectedWorker != null) {
      let absenceHours = 0;
      let absenceMinutes = 0;
      filteredAbsenceEntries = filteredAbsenceEntries.filter(
        (x) => x.user == this.selectedWorker
      );

      filteredAbsenceEntries.forEach((item) => {
        absenceHours += 8;
      });
      this.sumAbsence = this.translate.instant('hours.absenceValue', {
        value: `${absenceHours}H`,
      });
      //this.sumAll += '/140:00H';
    }

    if (this.showAbsenceEntries && this.selectedConSite == null) {
      filteredAbsenceEntries.forEach((item) => {
        this.filteredWorkListEntries.push({
          id: item.id,
          date: item.date,
          name: this.state.getUserDisplayName(item.user),
          hours: '-',
          isAbsence: true,
          approved: item.approvedBy != null,
          workingHoursComplete: true,
        });
      });
    }
    this.filteredWorkListEntries.sort((one, two) =>
      one?.date > two?.date ? -1 : 1
    );
  }

  async addButtonClicked() {
    await this.modalService.openHoursFormModal(null, this.state.isBigScreen);
  }

  async documentButtonClicked(type: string) {
    let req: WorkingOverviewRequest = {};
    req.type = type;
    req.constructionSiteId = this.selectedConSite ? this.selectedConSite : '';
    req.month = this.selectedMonth != null ? this.selectedMonth.toString() : '';
    req.users = this.selectedWorker ? [this.selectedWorker] : [];
    req.year = this.selectedYear;
    if (type == 'export') {
      const { data, role } = await this.modalService.createExportModal(
        `${this.toolService.getMonthName(this.selectedMonth)} ${
          this.selectedYear
        }`,
        this.selectedMonth,
        this.selectedYear
      );
      if (role == ModalRole.Cancel || role == ModalRole.Backdrop) {
        return;
      }
      req.users = data;
    }
    this.state.buildHourOverview(req);
  }

  async openContextMenu(event: MouseEvent) {
    let additionalOptions: ContextMenuOption[] = [
      {
        icon: 'construct-outline',
        label: this.translate.instant('hours.monthlyByConstructionSite'),
        value: 'construction-site',
      },
      {
        icon: 'person-outline',
        label: this.translate.instant('hours.monthlyByEmployee'),
        value: 'user',
      },
    ];
    if (!this.state.isMobile && this.state.userHasMinType(1)) {
      additionalOptions.push({
        icon: 'download-outline',
        label: this.translate.instant('hours.exportForAddison'),
        value: 'export',
      });
    }
    const { data, role } = await this.state.openPopover(
      event,
      false,
      additionalOptions
    );
    if (data) this.documentButtonClicked(data);
  }
}
